/**
 * Widget origin configuration — build-time defaults + runtime overrides.
 *
 * Build-time defaults come from esbuild `define` in `scripts/build.ts`;
 * the build script reads `POLST_APP_ORIGIN` / `POLST_API_ORIGIN` at
 * build time and bakes them into the bundle, falling back to the
 * production polst.app hosts when unset.
 *
 * Host pages can override at runtime via
 * `window.Polst.configure({ origins: { apiOrigin, appOrigin } })`.
 * Overrides win over build-time defaults on every getter read, so
 * tests and self-hosters can swap hosts without rebuilding.
 */

declare const __POLST_APP_ORIGIN__: string;
declare const __POLST_API_ORIGIN__: string;

const FALLBACK_APP_ORIGIN = "https://polst.app";
const FALLBACK_API_ORIGIN = "https://api.polst.app";

function resolveInjected(injected: string | undefined, fallback: string): string {
  return injected && injected.length > 0 ? injected : fallback;
}

export const DEFAULT_APP_ORIGIN = resolveInjected(
  typeof __POLST_APP_ORIGIN__ !== "undefined" ? __POLST_APP_ORIGIN__ : undefined,
  FALLBACK_APP_ORIGIN,
);
export const DEFAULT_API_ORIGIN = resolveInjected(
  typeof __POLST_API_ORIGIN__ !== "undefined" ? __POLST_API_ORIGIN__ : undefined,
  FALLBACK_API_ORIGIN,
);

export interface OriginOverrides {
  /** Marketing / share-link host (default polst.app). */
  appOrigin?: string;
  /** Public REST API host (default api.polst.app). */
  apiOrigin?: string;
}

interface MutableOverrides {
  appOrigin?: string;
  apiOrigin?: string;
}

const overrides: MutableOverrides = {};

export function configureOrigins(opts: OriginOverrides): void {
  if (opts.appOrigin !== undefined) {
    overrides.appOrigin = opts.appOrigin || undefined;
  }
  if (opts.apiOrigin !== undefined) {
    overrides.apiOrigin = opts.apiOrigin || undefined;
  }
}

export function resetOriginsForTesting(): void {
  overrides.appOrigin = undefined;
  overrides.apiOrigin = undefined;
}

export function getAppOrigin(): string {
  return overrides.appOrigin ?? DEFAULT_APP_ORIGIN;
}

export function getApiOrigin(): string {
  return overrides.apiOrigin ?? DEFAULT_API_ORIGIN;
}
